import { BRIDGE_ERROR, BRIDGE_METHODS, NATIVE_BIOMETRIC_PLUGIN } from '../../constants/bridge';
import { fail, ok } from '../result';

const IMPACT = { light: 'LIGHT', medium: 'MEDIUM', heavy: 'HEAVY' };
const NOTIFY = { success: 'SUCCESS', warning: 'WARNING', error: 'ERROR' };
const BIOMETRY_KIND = { face: 'face', fingerprint: 'fingerprint', iris: 'iris', device: 'device' };
const NATIVE_CODES = new Set([
    BRIDGE_ERROR.CANCELLED,
    BRIDGE_ERROR.NOT_ENROLLED,
    BRIDGE_ERROR.LOCKED_OUT,
    BRIDGE_ERROR.UNAVAILABLE,
    BRIDGE_ERROR.PERMISSION_DENIED,
    BRIDGE_ERROR.TIMEOUT,
]);
const INT32_MAX = 2_147_483_647;

function permissionState(display) {
    if (display === 'granted' || display === 'denied') return display;
    if (display === 'prompt' || display === 'prompt-with-rationale') return 'prompt';
    return 'unsupported';
}

// Capacitor rejects plugin calls with { code, message }. Native samples in
// /native use the same codes as the JS contract; anything else is FAILED.
function fromNativeError(error) {
    const code = error?.code;
    if (NATIVE_CODES.has(code)) return fail(code);
    if (code === 'UNIMPLEMENTED' || code === 'UNAVAILABLE') return fail(BRIDGE_ERROR.UNAVAILABLE);
    if (/cancel/i.test(String(error?.message || ''))) return fail(BRIDGE_ERROR.CANCELLED);
    return fail(BRIDGE_ERROR.FAILED);
}

function createIdSource() {
    let counter = Math.floor(Date.now() / 1000) % 1_000_000;
    return () => {
        counter = (counter % INT32_MAX) + 1;
        return counter;
    };
}

export function createCapacitorAdapter({ capacitor, share, haptics, notifications, biometric }) {
    const has = (name) => capacitor.isPluginAvailable(name);
    const nextNotificationId = createIdSource();

    async function biometricStatus() {
        if (!biometric || !has(NATIVE_BIOMETRIC_PLUGIN)) {
            return { available: false, kind: 'none', reason: 'El plugin biométrico no está registrado en esta app.' };
        }
        try {
            const status = await biometric.isAvailable();
            const kind = BIOMETRY_KIND[status?.biometryType] || 'none';
            if (status?.available === true) return { available: true, kind };
            return { available: false, kind, reason: typeof status?.reason === 'string' ? status.reason.slice(0, 200) : undefined };
        } catch {
            return { available: false, kind: 'none', reason: 'No se pudo consultar la biometría.' };
        }
    }

    async function readPermission() {
        const status = await notifications.checkPermissions();
        return permissionState(status?.display);
    }

    const methods = {
        [BRIDGE_METHODS.CAPABILITIES]: async () => {
            let canShare = false;
            if (has('Share')) {
                try {
                    canShare = (await share.canShare())?.value === true;
                } catch {
                    canShare = false;
                }
            }
            const platform = capacitor.getPlatform();
            return ok({
                platform: platform === 'ios' || platform === 'android' ? platform : 'web',
                host: 'capacitor',
                share: canShare,
                haptics: has('Haptics'),
                localNotifications: has('LocalNotifications'),
                biometric: await biometricStatus(),
            });
        },

        [BRIDGE_METHODS.BIOMETRIC_AUTHENTICATE]: async ({ reason }, { signal }) => {
            if (!biometric || !has(NATIVE_BIOMETRIC_PLUGIN)) return fail(BRIDGE_ERROR.UNAVAILABLE);
            // Timeout or caller cancellation must dismiss the system sheet,
            // not just stop waiting for it.
            const handleAbort = () => {
                biometric.cancel?.().catch?.(() => {});
            };
            signal?.addEventListener('abort', handleAbort, { once: true });
            try {
                const response = await biometric.authenticate({ reason });
                if (response?.verified !== true) return fail(BRIDGE_ERROR.FAILED);
                return ok({ verified: true, kind: BIOMETRY_KIND[response.biometryType] || 'device' });
            } catch (error) {
                return fromNativeError(error);
            } finally {
                signal?.removeEventListener('abort', handleAbort);
            }
        },

        [BRIDGE_METHODS.NOTIFICATION_PERMISSION]: async () => {
            if (!has('LocalNotifications')) return ok({ state: 'unsupported' });
            try {
                return ok({ state: await readPermission() });
            } catch (error) {
                return fromNativeError(error);
            }
        },

        [BRIDGE_METHODS.NOTIFICATION_REQUEST]: async () => {
            if (!has('LocalNotifications')) return fail(BRIDGE_ERROR.UNAVAILABLE);
            try {
                const status = await notifications.requestPermissions();
                return ok({ state: permissionState(status?.display) });
            } catch (error) {
                return fromNativeError(error);
            }
        },

        [BRIDGE_METHODS.NOTIFICATION_LOCAL]: async ({ title, body }) => {
            if (!has('LocalNotifications')) return fail(BRIDGE_ERROR.UNAVAILABLE);
            try {
                if (await readPermission() !== 'granted') return fail(BRIDGE_ERROR.PERMISSION_DENIED);
                // Since plugin 8.3 an exact (default) notification may open the
                // Android alarm settings screen; an immediate test never needs it.
                await notifications.schedule({
                    notifications: [{ id: nextNotificationId(), title, body: body || '', isExactNotification: false }],
                });
                return ok({ scheduled: true });
            } catch (error) {
                return fromNativeError(error);
            }
        },

        [BRIDGE_METHODS.SHARE]: async (payload) => {
            if (!has('Share')) return fail(BRIDGE_ERROR.UNAVAILABLE);
            try {
                await share.share(payload);
                return ok({ shared: true });
            } catch (error) {
                return fromNativeError(error);
            }
        },

        [BRIDGE_METHODS.HAPTIC]: async ({ style }) => {
            if (!has('Haptics')) return fail(BRIDGE_ERROR.UNAVAILABLE);
            try {
                if (IMPACT[style]) await haptics.impact({ style: IMPACT[style] });
                else await haptics.notification({ type: NOTIFY[style] });
                return ok({ done: true });
            } catch (error) {
                return fromNativeError(error);
            }
        },
    };

    return { host: 'capacitor', methods };
}
